import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PinStrip from "../components/PinStrip";
import { getPaidSession, isPaid } from "../lib/paidDownloads";

/**
 * Strict vertical 4-box stack for every recipe page.
 *
 * Page 1 (on-site & downloadable PDF share this layout):
 *   1. TOP BOX     — Full-width real food image (dynamic height).
 *   2. SECOND BOX  — Definition.
 *   3. THIRD BOX   — Ingredients.
 *   4. FOURTH BOX  — Kitchen Table (kitchenware + purpose).
 *
 * Page 2 (below):
 *   · Method (numbered steps)
 *   · Critical Points — The WOW Factor (bullets + closing italic tagline).
 *
 * A small stat line sits directly under the title
 * ("Prep 15 min · Cook 30 min · Serves 4"). Body text stays at a fixed size
 * (12pt equivalent on the PDF, 16px on-site). No section auto-shrinks.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Decorative leaf sprig used to flank section titles
function LeafSprig({ flip = false }) {
  return (
    <svg
      width="42" height="14" viewBox="0 0 42 14" aria-hidden="true"
      style={{ transform: flip ? "scaleX(-1)" : undefined, color: "var(--mustard, #DDA73C)", flexShrink: 0 }}
    >
      <g fill="currentColor">
        <path d="M2 7 Q 12 -2 20 4 Q 22 6 24 7 L 22 8 Q 20 9 18 8 Q 10 4 4 8 Z" />
        <path d="M26 7 L 40 7" stroke="currentColor" strokeWidth="1" fill="none" />
        <circle cx="26" cy="7" r="1.3" />
      </g>
    </svg>
  );
}

function SectionTitle({ children, color }) {
  return (
    <div className="flex items-center justify-center gap-3 md:gap-4">
      <LeafSprig />
      <h2
        className="font-display text-3xl md:text-4xl leading-none tracking-tight text-center"
        style={{ color: color || "var(--olive, #5C6B46)" }}
      >
        {children}
      </h2>
      <LeafSprig flip />
    </div>
  );
}

function Card({ children, className = "" }) {
  return (
    <section
      className={`relative border-2 rounded-2xl px-6 md:px-10 py-6 md:py-8 ${className}`}
      style={{ borderColor: "var(--olive, #5C6B46)", background: "rgba(244,241,235,0.55)" }}
    >
      {children}
    </section>
  );
}

export default function RecipeDetail() {
  const { slug } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [term, setTerm] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.getRecipe(slug);
        setRecipe(r);
        if (r.related_term_slug) {
          try { setTerm(await api.getEntry(r.related_term_slug)); } catch {}
        }
      } catch { setNotFound(true); }
    })();
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="recipe-not-found">
      <div className="font-display italic text-4xl">Recipe not found.</div>
      <Link to="/recipes" className="btn-primary mt-6 inline-flex">Back to recipes</Link>
    </div>
  );
  if (!recipe) return <div className="max-w-3xl mx-auto px-6 py-24 font-ui uppercase tracking-widest text-sm opacity-60" data-testid="recipe-loading">Loading…</div>;

  const kwDetails = recipe.kitchenware_details && recipe.kitchenware_details.length > 0
    ? recipe.kitchenware_details
    : (recipe.kitchenware || []).map((item) => ({ item, purpose: "" }));
  const hasAnyPurpose = kwDetails.some((row) => row && row.purpose);

  const definition = (term?.definition || term?.formal_definition || recipe.subtitle
    || "A short definition for this recipe will appear here soon.").trim();

  const ingredientsProse = (recipe.ingredients_prose || "").trim()
    || ((recipe.ingredients || []).map((i) => {
      const q = (i.quantity || "").trim();
      const it = (i.item || "").trim();
      return q ? `${q} ${it}` : it;
    }).filter(Boolean).join("; ") + ".");

  const critical = recipe.critical_points || [];
  const wowTag = (recipe.wow_factor_tagline || "").trim();
  // Prefer the linked glossary entry's curated real photo over the recipe's
  // own image (which is often a text-card placeholder).
  const imageSrc = (term?.image_url || recipe.image_url)
    || "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 5 4'><rect width='5' height='4' fill='%23f0c96b'/></svg>";

  return (
    <article className="fade-in" data-testid="recipe-detail" style={{ background: "var(--bg-primary, #F4F1EB)" }}>
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <Link to="/recipes" className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest opacity-70 hover-underline mb-6 no-print" data-testid="back-to-recipes">
          <ArrowLeft size={14} /> Back to recipes
        </Link>

        {/* Title + stat line */}
        <header className="mb-8 md:mb-10 text-center">
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-2">
            <LeafSprig />
            <div className="font-ui text-[10px] md:text-xs uppercase tracking-[0.35em] opacity-70" style={{ color: "var(--olive, #5C6B46)" }}>
              Bite &amp; Banter — Kitchen Desk
            </div>
            <LeafSprig flip />
          </div>
          <h1
            className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
            style={{ color: "var(--olive, #5C6B46)" }}
            data-testid="recipe-title"
          >
            {recipe.title}
          </h1>
          {recipe.subtitle && (
            <p className="font-body italic text-lg md:text-xl opacity-70 mt-3 max-w-3xl mx-auto" data-testid="recipe-subtitle">
              {recipe.subtitle}
            </p>
          )}
          <div
            className="mt-4 flex justify-center items-center gap-3 md:gap-4 font-ui text-xs md:text-sm uppercase tracking-[0.2em]"
            style={{ color: "var(--olive-ink, #3F4B31)" }}
            data-testid="recipe-stat-line"
          >
            <span>Prep {recipe.prep_time || "—"}</span>
            <span aria-hidden="true">·</span>
            <span>Cook {recipe.cook_time || "—"}</span>
            <span aria-hidden="true">·</span>
            <span>Serves {recipe.servings || "—"}</span>
          </div>
        </header>

        {/* ============================================================
            STRICT VERTICAL 4-BOX STACK (Page 1)
        ============================================================ */}

        {/* 1. TOP BOX — Full-width food image */}
        <div
          className="mb-6 rounded-2xl overflow-hidden border-2"
          style={{ borderColor: "var(--olive, #5C6B46)" }}
          data-testid="recipe-hero-wrapper"
        >
          <img
            src={imageSrc}
            alt={recipe.title}
            className="w-full h-auto object-cover"
            style={{ aspectRatio: "16/9", display: "block" }}
            onError={(e) => { e.currentTarget.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 5 4'><rect width='5' height='4' fill='%23f0c96b'/></svg>"; }}
            data-testid="recipe-hero-image"
          />
        </div>

        {/* 2. SECOND BOX — Definition */}
        <Card className="mb-6">
          <SectionTitle>Definition</SectionTitle>
          <p className="font-body text-base md:text-lg leading-relaxed text-center mt-4 max-w-4xl mx-auto" data-testid="recipe-definition">
            {definition}
          </p>
        </Card>

        {/* 3. THIRD BOX — Ingredients */}
        <Card className="mb-6">
          <SectionTitle>Ingredients</SectionTitle>
          <p className="font-body text-base md:text-lg leading-relaxed text-center mt-4 max-w-4xl mx-auto" data-testid="recipe-ingredients-prose">
            {ingredientsProse}
          </p>
        </Card>

        {/* 4. FOURTH BOX — Kitchen Table (Cooking Table) */}
        <Card className="mb-8">
          <SectionTitle>Kitchen Table</SectionTitle>
          {kwDetails.length === 0 ? (
            <p className="font-body italic opacity-60 text-center mt-4" data-testid="recipe-cooking-empty">
              A tailored kitchen-material list is being prepared for this recipe.
            </p>
          ) : hasAnyPurpose ? (
            <div className="mt-5 max-w-4xl mx-auto overflow-hidden rounded-lg border" style={{ borderColor: "var(--olive, #5C6B46)" }}>
              <div className="grid grid-cols-5 font-ui text-xs md:text-sm uppercase tracking-[0.15em]" style={{ background: "var(--olive, #5C6B46)", color: "#fff" }}>
                <div className="col-span-2 px-3 md:px-5 py-2 md:py-3">Kitchen material needed</div>
                <div className="col-span-3 px-3 md:px-5 py-2 md:py-3 border-l" style={{ borderColor: "rgba(255,255,255,0.25)" }}>Purpose</div>
              </div>
              {kwDetails.map((row, idx) => (
                <div key={idx} className="grid grid-cols-5 border-t" style={{ borderColor: "rgba(92,107,70,0.35)", background: idx % 2 ? "rgba(240,201,107,0.06)" : "transparent" }} data-testid={`recipe-cooking-row-${idx}`}>
                  <div className="col-span-2 px-3 md:px-5 py-2 md:py-3 font-body text-base leading-snug">{row.item}</div>
                  <div className="col-span-3 px-3 md:px-5 py-2 md:py-3 font-body text-base leading-snug italic opacity-90 border-l" style={{ borderColor: "rgba(92,107,70,0.35)" }}>
                    {row.purpose || <span className="opacity-40 not-italic">—</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <ul className="mt-5 space-y-2 max-w-3xl mx-auto" data-testid="recipe-cooking-list">
              {kwDetails.map((row, idx) => (
                <li key={idx} className="flex gap-3 border-b border-dashed border-black/15 pb-2">
                  <span className="font-display italic text-xl leading-none pt-1 shrink-0" style={{ color: "var(--tomato)" }}>·</span>
                  <span className="font-body text-base md:text-lg flex-1">{row.item}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* ============================================================
            PAGE 2 CONTENT
        ============================================================ */}

        {/* Method */}
        <Card className="mb-6">
          <SectionTitle>Method</SectionTitle>
          <ol className="mt-5 space-y-3 max-w-4xl mx-auto pl-6 md:pl-10 list-decimal marker:font-display marker:italic marker:text-2xl" style={{ color: "var(--ink)" }} data-testid="recipe-method">
            {(recipe.steps || []).map((s, idx) => (
              <li key={idx} className="font-body text-base md:text-lg leading-relaxed pl-2">
                {s.text || s}
              </li>
            ))}
          </ol>
        </Card>

        {/* Critical Points — The WOW Factor */}
        <Card className="mb-6">
          <SectionTitle color="#087A46">
            Critical Points <span className="opacity-90">— The WOW Factor</span>
          </SectionTitle>
          {critical.length > 0 ? (
            <ul className="mt-5 space-y-3 max-w-4xl mx-auto" data-testid="recipe-critical-points">
              {critical.map((tip, idx) => (
                <li key={idx} className="flex gap-3">
                  <span className="font-display italic text-2xl leading-none pt-1 shrink-0" style={{ color: "var(--tomato, #D34F44)" }}>·</span>
                  <span className="font-body text-base md:text-lg leading-relaxed">{tip}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-body italic opacity-60 text-center mt-4" data-testid="recipe-critical-empty">
              Chef's critical points and the wow-factor tagline for this recipe are being written and will appear here soon.
            </p>
          )}
          {wowTag && (
            <p className="font-display italic text-lg md:text-xl text-center mt-6 max-w-3xl mx-auto" style={{ color: "#087A46" }} data-testid="recipe-wow-tagline">
              {wowTag}
            </p>
          )}
        </Card>

        {/* Closing line + download callout */}
        {recipe.closing_line && (
          <p className="font-display italic text-2xl md:text-3xl text-center max-w-3xl mx-auto mt-8" style={{ color: "var(--olive, #5C6B46)" }} data-testid="recipe-closing-line">
            — {recipe.closing_line}
          </p>
        )}

        {recipe.related_term_slug && (
          <div className="mt-10 flex justify-center gap-3 flex-wrap no-print">
            <DownloadButton slug={recipe.related_term_slug} />
            <Link to={`/term/${recipe.related_term_slug}`} className="btn-primary btn-outline" data-testid="recipe-view-term">
              View the glossary entry
            </Link>
          </div>
        )}

        <PinStrip />
      </div>

      {/* Sticky mobile-only download bar — keeps the paywall CTA one tap away
          on phones without stealing space from the reading experience.
          Rendered via portal to escape any parent that creates a stacking
          context (the fade-in animation applies `transform` on <main>). */}
      {recipe.related_term_slug && createPortal(
        (
          <div
            className="md:hidden fixed bottom-0 left-0 right-0 z-40 no-print"
            style={{
              background: "rgba(244,241,235,0.96)",
              backdropFilter: "blur(8px)",
              borderTop: "1px solid rgba(26,26,26,0.15)",
              paddingBottom: "max(env(safe-area-inset-bottom), 8px)",
            }}
            data-testid="recipe-mobile-cta-bar"
          >
            <div className="px-4 py-3 flex gap-2">
              <DownloadButton slug={recipe.related_term_slug} fullWidth testId="recipe-mobile-download-btn" />
            </div>
          </div>
        ),
        document.body,
      )}
      {/* Spacer so the sticky bar doesn't cover the footer copyright on mobile */}
      {recipe.related_term_slug && <div className="md:hidden h-20" aria-hidden="true" />}
    </article>
  );
}

// ---------------------------------------------------------------------------
// DownloadButton — the paywall entry point for a single-recipe PDF.
// ---------------------------------------------------------------------------
function DownloadButton({ slug, fullWidth = false, testId = "recipe-download-btn" }) {
  const [busy, setBusy] = useState(false);
  const paid = isPaid(slug);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    if (paid) {
      const sid = getPaidSession(slug);
      const url = `${BACKEND_URL}/api/glossary/${slug}/download?session_id=${encodeURIComponent(sid)}`;
      try { (window.top || window).location.href = url; } catch { window.location.href = url; }
      setBusy(false);
      return;
    }
    try {
      const { checkout_url } = await api.createCheckoutSession(slug);
      try { (window.top || window).location.href = checkout_url; }
      catch { window.location.href = checkout_url; }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Couldn't open Stripe checkout");
      setBusy(false);
    }
  };

  return (
    <button
      onClick={start}
      disabled={busy}
      className={`btn-primary btn-accent disabled:opacity-60 ${fullWidth ? "w-full justify-center" : ""}`}
      data-testid={testId}
    >
      {busy ? (
        <><Loader2 size={16} className="animate-spin" /> Opening…</>
      ) : paid ? (
        <><Download size={16} /> Download recipe</>
      ) : (
        <><Lock size={14} /> Unlock recipe — $2.25</>
      )}
    </button>
  );
}
